import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import { exportPlanningAction, postExportDataAction } from '../../actions/shift';
import ExportButton from '../../components/ExportButton';
import { IShift } from '../../types/shift';
import { ILine, IPole, IPool } from '../../types/structure';
import styles from './export.module.scss';
import { getBrightness, getWeekNumber } from '../../utils';

const Step = ({
  step,
  labels,
  isLast,
  legend,
  shiftWidth,
  activitiesOptions = [],
}: {
  step: IShift,
  labels: any,
  isLast: boolean,
  legend: any,
  shiftWidth: number,
  activitiesOptions: any,
}) => {
  const other = activitiesOptions?.find((ac : any) => ac.value === step.activity);
  const leftIndex = labels.findIndex((l: any) => l.label === step.slices[0]);
  const color = step.structure.poles.find(
    (p: IPole) => p._id === step.pole,
  )?.activities.find((a: any) => a.label === step.activity)?.color
  || step.structure.poles.find((p: IPole) => p._id === step.pole)?.color
  || other?.color || '#FF0000';

  const width = step.slices.length * shiftWidth;

  const activity = other?.label
  || step.activity
  || legend.find((l: any) => l.value === step.activity)?.label;

  return <div className={`${styles.step} ${isLast ? styles.isLast : ''}`}
    style={{
      backgroundColor: color,
      width,
      left: leftIndex * shiftWidth,
    }}
  >
    <p style={{
      backgroundColor: color,
      color: getBrightness(color) ? '#000000' : '#FFFFFF',
    }}>{activity}</p>
  </div>;
};

const ShiftsRow = (
  {
    shifts,
    labels,
    isLast,
    hours = null,
    legend,
    shiftWidth,
    activitiesOptions,
  }
  :{
    shifts: IShift[],
    labels: any,
    isLast: boolean
    hours?: number | null,
    legend: any,
    shiftWidth: number,
    activitiesOptions: any
  },
) => (
    <div className={styles.shifts}>
      <div className={styles.slots}
      >
        {labels?.map((d: any) => {
          const isHour = d.label?.includes(':00');
          const isHalf = (d.label?.includes(':15') || d.label?.includes(':30') || d.label?.includes(':45'));
          return (
            <div
              key={d.label}
              className={`${styles.slot} ${isHour ? styles.hour : ''} ${isHalf ? styles.half : ''}`}
              style={{ width: shiftWidth }}
            >
            </div>
          );
        })}
        {shifts.map((s: IShift) => <Step
          key={s._id}
          step={s}
          labels={labels}
          isLast={isLast}
          legend={legend}
          shiftWidth={shiftWidth}
          activitiesOptions={activitiesOptions}
        />)}
      </div>
      <div className={styles.count}>
        {hours && <p>{hours}</p>}
      </div>
    </div>
);

const Export = ({ isPreview = false }:{ isPreview?: boolean }) => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const [elements, setElements] = useState([]);
  const [usersTotal, setUsersTotal] = useState<any[]>([]);
  const [activitiesOptions, setActivitesOptions] = useState<any[]>([]);
  const [legend, setLegend] = useState([]);
  const [fileName, setFileName] = useState<string>('planning.pdf');
  const { token } = params;
  const structure = searchParams.get('structure');
  const startDate = searchParams.get('startDate');
  const endDate = searchParams.get('endDate');
  const poles = searchParams.get('poles');
  const planningView = searchParams.get('planningView');

  const [shiftWidth, setShiftWidth] = useState(6);
  const downloadFile = async () => {
    const res = await exportPlanningAction(dispatch, {
      structure,
      startDate,
      poles: poles?.split(','),
      endDate,
      planningView,
    }, fileName);
    return res;
  };

  const getExportData = async () => {
    if (token && startDate && structure) {
      const res = await postExportDataAction(dispatch, token, {
        structure,
        startDate,
        poles: poles?.split(','),
        endDate,
      });
      if (res.data) {
        const structureName = res.data?.structure?.name;
        const weekNumber = endDate && getWeekNumber(new Date(endDate));
        const legendData : any = [];
        const activities = res?.data?.activitiesOptions || [];
        setActivitesOptions(activities);

        const pools = res.data?.pools;
        const users = res.data?.users;
        const rows : any = [];
        let countRow = 0;
        const header = {
          type: 'header',
          label: `PLANNING ${structureName} - ${endDate ? `Semaine ${weekNumber} -` : ''}`,
        };

        const dateSpace = {
          type: 'space',
        };

        const dates : any = [];
        const daysSlots = {
          start: 0,
          end: 0,
        };
        const shifts = res.data?.shifts.filter((s:IShift) => {
          if (planningView === 'global') {
            if (s.line) return s;
            if (s.user && !users.find((u:any) => u._id === s.user)) {
              return null;
            }
            return s;
          }
          if (planningView === 'users') {
            if (s.line) return null;
            if (s.user && !users.find((u:any) => u._id === s.user)) {
              return null;
            }
            return s;
          }

          if (planningView === 'pools') {
            return s.line ? s : null;
          }
          return s;
        });
        const hoursLabels = res.data?.hours;
        const usersTable : any[] = [];
        // Create legend

        shifts.forEach((shift: IShift) => {
          const shiftTimes = {
            start: hoursLabels.find((s:any) => s.label === shift.slices[0]).start,
            end: hoursLabels.find((s:any) => s.label === shift.slices[shift.slices.length - 1]).end,
          };
          // find First en last hours on shift
          if (shiftTimes.start < daysSlots.start || daysSlots.start === 0) {
            daysSlots.start = shiftTimes.start;
          }
          if (shiftTimes.end > daysSlots.end || daysSlots.end === 0) {
            daysSlots.end = shiftTimes.end;
          }
          if (!legendData.find((l:any) => l.value === shift.activity)) {
            const other = activitiesOptions?.find((ac : any) => ac.value === shift.activity
            || ac.label === shift.activity);
            const color = res.data?.structure.poles.find(
              (p: IPole) => p._id === shift.pole,
            )?.activities.find((a: any) => a.label === shift.activity)?.color
            || shift.structure.poles.find((p: IPole) => p._id === shift.pole)?.color
            || other?.color
            || res.data.legend.find((l: any) => l.value === shift.activity)?.color
            || '#FF0000';

            const label = other?.label
            || res.data.legend.find((l: any) => l.value === shift.activity)?.label
            || shift.activity;

            legendData.push({
              value: shift.activity,
              color,
              label,
            });
          }
        });

        // find First en last hours to display
        const firstHour = hoursLabels.find(
          (h:any) => h.start === daysSlots.start,
        )?.label?.split(':');

        const firstIndex = hoursLabels.findIndex(
          (h:any) => h.label === `${firstHour[0]}:00`,
        );
        const lastIndex = hoursLabels.findIndex(
          (h:any) => h.end >= daysSlots.end && h.label?.endsWith(':00'),
        );

        // filter hours labels
        const labels = res.data?.hours.filter(
          (h:any, i: number) => i >= firstIndex && i <= lastIndex,
        );

        // clac slot size
        setShiftWidth(1500 / labels.length);

        if (endDate) {
          let current = 0;
          const end = 6;
          while (current <= end) {
            const newDate = new Date(new Date(startDate)
              .setDate(new Date(startDate).getDate() + current));
            dates.push({
              date: newDate,
              dateLabel: new Intl.DateTimeFormat('fr', {
                weekday: 'short',
                month: 'long',
                day: 'numeric',
              }).format(newDate),
              labels,
              shifts: shifts?.filter(
                (s: IShift) => new Date(s.date).toISOString() === newDate.toISOString(),
              ),
            });
            if (!endDate) {
              current = 6;
            } else {
              current += 1;
            }
          }
        } else {
          const newDate = new Date(startDate);
          dates.push({
            date: newDate,
            dateLabel: new Intl.DateTimeFormat('fr', {
              weekday: 'short',
              month: 'long',
              day: 'numeric',
            }).format(newDate),
            labels,
            shifts: shifts?.filter(
              (s: IShift) => new Date(s.date).toISOString() === newDate.toISOString(),
            ),
          });
        }
        let name = 'planning';
        if (dates.length > 1) {
          const endDay = new Intl.DateTimeFormat('fr', {
            weekday: 'short',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          }).format(dates[dates.length - 1].date);
          header.label += `${dates[0].dateLabel} au ${endDay}`;
          name += `-${dates[0].dateLabel}-au-${endDay}.pdf`;
        } else {
          const day = new Intl.DateTimeFormat('fr', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          }).format(new Date(startDate));
          header.label += `${day}`;
          name += `-${day}.pdf`;
        }
        setFileName(name.replaceAll(' ', '-'));
        rows.push(header);
        countRow = 3;
        dates.forEach((day: any, i: number) => {
          if (i > 0) {
            // rows.push(breakPage);
            rows.push(dateSpace);
          }
          const dayLabel = {
            type: 'label',
            day: day.dateLabel,
            labels,
          };
          rows.push(dayLabel);
          countRow += 1;
          pools.forEach((p: IPool) => {
            const pool : any = {
              type: 'pool',
              pool: p.name,
              lines: [],
            };
            p.lines.forEach((l: ILine) => {
              pool.lines.push({
                label: l.name,
                key: `${l.name}-${day.dateLabel}`,
                shifts: day?.shifts?.filter((s:IShift) => s.line === l._id),
                labels,
              });
            });

            countRow += (pool.lines.length + users.length);
            console.log(countRow * 4 > 248);
            // if (countRow * 4 > 240) {
            //   rows.splice(rows.length - 1, 0, header);
            //   rows.splice(rows.length - 2, 0, breakPage);
            //   countRow = 3;
            // }
            rows.push(pool);
          });
          if (planningView === 'pools') {
            rows.push({ type: 'pool-end' });
          }
          users.forEach((u: any, userIndex: number) => {
            const absencesOptions = u.contract?.isAnnualized ? ['paid vacation'] : [];
            const userName = `${u?.profile?.firstName} ${u?.profile?.lastName}`;
            const user : any = {
              type: 'user',
              user: userName,
              color: u.color,
              shifts: day?.shifts?.filter((s:IShift) => s.user === u._id),
              labels,
              isLast: userIndex === users.length - 1,
            };
            const slices = day?.shifts?.filter(
              (s:IShift) => s.user === u._id
              && !absencesOptions.find((a: string) => a === s.activity),
            ).map((s: IShift) => s.slices);
            const hours = slices.flat().length / 12;
            const userTableIndex : number = usersTable.findIndex((ut) => ut._id === u._id);
            if (hours > 0) {
              const minutes = (hours - Math.floor(hours)) * 60;
              user.hours = `${Math.floor(hours)}H${minutes > 0 ? minutes.toFixed() : ''}`;
              if (userTableIndex !== -1) {
                usersTable[userTableIndex].total += hours;
              }
              if (userTableIndex === -1) {
                usersTable.push({
                  _id: u._id,
                  name: userName,
                  color: u.color,
                  total: hours,
                });
              }
            }
            rows.push(user);
          });
        });

        const usersTableFormated : any[] = usersTable.map((u) => {
          const hours = u?.total;
          const minutes = (hours - Math.floor(hours)) * 60;
          return {
            ...u,
            hours: `${Math.floor(hours)}H${minutes > 0 ? minutes.toFixed() : ''}`,
          };
        });
        console.log(planningView);
        setUsersTotal(usersTableFormated);
        setElements(rows);
        setLegend(legendData);
      }
    }
  };

  useEffect(() => {
    if (token && startDate && structure) {
      getExportData();
    }
  }, []);
  console.log(usersTotal);
  return (
    <div className={`${styles.container} ${isPreview ? styles.preview : ''}`}>
      <div id="export" className={styles.export}>
        {elements.map((element : any, i: number) => <div
          key={`element-${i}`}
          className={styles.element}
          >
          {element.type === 'header'
            && <div className={styles.header}>
              <h2>{element.label}</h2>
            </div>
          }
          {element.type === 'label'
            && <div className={styles.label}>
              <div className={styles.day}>
                {element.day}
              </div>
              <div className={styles.hours}>
                {element?.labels?.filter((l: any) => l.label !== '02:00').map((d: any) => {
                  const splited = d.label.split(':');
                  let hour = d.label?.includes(':00') && splited[0];
                  if (hour && hour.charAt(0) === '0') {
                    hour = hour.slice(1);
                  }
                  if (hour) {
                    hour += 'H';
                  }
                  let minutes = d.label?.includes(':30') && d.label;
                  if (minutes && minutes.charAt(0) === '0') {
                    minutes = minutes.slice(1);
                  }
                  const isActive = (d.label?.includes(':00') || d.label?.includes(':15') || d.label?.includes(':30') || d.label?.includes(':45'));
                  return (
                    <div
                      key={d.label}
                      className={`${styles.slot} ${isActive ? styles.active : ''}`}
                      style={{ width: shiftWidth }}
                    >
                      {hour && <p className={styles.hour}>{hour}</p>}
                      {minutes && <p className={styles.minutes}>{minutes}</p>}
                    </div>
                  );
                })}
              </div>
            </div>
          }
          {element.type === 'pool' && planningView !== 'users'
            && <div className={styles['pool-row']}>
              <div className={styles.pool}>
                <p>{element.pool}</p>
              </div>
              <div className={styles.lines}>
                {element.lines.map((l: any, lineIndex: number) => <div
                  key={l.key}
                  className={styles.line}
                >
                  <div className={styles['line-label']}>
                    <p>{l.label}</p>
                  </div>
                  <div className={styles.shifts}>
                    <ShiftsRow
                      shifts={l.shifts}
                      labels={l.labels}
                      isLast={lineIndex === element.lines.length - 1 }
                      legend={legend}
                      shiftWidth={shiftWidth}
                      activitiesOptions={activitiesOptions}
                    />
                  </div>
                </div>)}
              </div>
            </div>
          }
          {element.type === 'user' && planningView !== 'pools'
            && <div className={`${styles['user-row']} ${element.isLast ? styles.isLast : ''}`}>
                <div className={styles.user}
                    style={{ backgroundColor: element?.color || 'white' }}
                  >
                  <p style={{ color: '#000000' }}>{element.user}</p>
                </div>
                <div className={styles.shifts}>
                  <ShiftsRow
                    shifts={element.shifts}
                    labels={element.labels}
                    activitiesOptions={activitiesOptions}
                    isLast={true}
                    hours={element?.hours}
                    legend={legend}
                    shiftWidth={shiftWidth}
                  />
                </div>
            </div>
          }
          {element.type === 'break'
            && <div className={styles.break}/>
          }
           {element.type === 'pool-end'
            && <div className={styles['pool-end']}/>
          }
          {element.type === 'space'
            && <div className={styles.space}/>
          }
        </div>)}
        {usersTotal?.length > 0 && planningView !== 'pools'
          && <div className={styles['users-table']}>
            <div className={styles.users}>
              {usersTotal.map((u: any) => <div
              key={u._id}
              className={styles.user}
              style={{ backgroundColor: u?.color || 'transparent' }}
            >
                <p style={{ color: '#000000' }}>{u.name}</p>
                <p style={{ color: '#000000' }}>{u.hours}</p>
              </div>)}
            </div>
            <div className={styles.legend}>
              <span>Légende</span>
              <div className={styles.items}>
                {legend.filter(
                  (a: any) => a.value !== 'rest'
                  && a.value !== 'sick'
                  && a.value !== 'Leave without pay'
                  && a.value !== 'conventional leave'
                  && a.value !== 'other'
                  && a.value !== 'dress up day'
                  && a.value !== 'unjustified absence',
                ).map((l : any) => <div
                  key={l.value}
                  className={styles.item}
                >
                <div
                  className={styles.color}
                  style={{
                    backgroundColor: l.color,
                  }}
                >
                </div>
                <p>{l.label}</p>
                </div>)}
              </div>
              </div>
          </div>
        }
        {usersTotal?.length === 0
          && <div className={`${styles['users-table']} ${styles.pools}`}>
            <div className={styles.legend}>
              <span>Légende</span>
              <div className={styles.items}>
                {legend.filter(
                  (a: any) => a.value !== 'rest'
                  && a.value !== 'sick'
                  && a.value !== 'Leave without pay'
                  && a.value !== 'conventional leave'
                  && a.value !== 'other'
                  && a.value !== 'dress up day'
                  && a.value !== 'unjustified absence',
                ).map((l : any) => <div
                  key={l.value}
                  className={styles.item}
                >
                <div
                  className={styles.color}
                  style={{
                    backgroundColor: l.color,
                  }}
                >
                </div>
                <p>{l.label}</p>
                </div>)}
              </div>
              </div>
          </div>
        }
      </div>
        {isPreview
          && <div className={styles.download}>
            <ExportButton
              callBack={downloadFile}
              isPreview
            />
          </div>
        }
    </div>
  );
};

export default Export;
